import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import ShiftPatternForm, {
    ShiftPatternApiData,
    type ShiftPatternFormStep,
} from "../components/ShiftPatternForm";
import type { TagMaster, TagFormValues } from "../components/TagSelectorPanel";
import {
    fetchShiftPattern,
    createShiftPattern,
    updateShiftPattern,
    deleteShiftPattern,
} from "../api/shiftPatterns";
import {
    fetchShiftPatternTags,
    fetchShiftPatternTagAssignments,
    createShiftPatternTag,
    updateShiftPatternTag,
    deleteShiftPatternTag,
    updateShiftPatternTagAssignments,
} from "../api/shiftPatternTags";
import { recordUserAction } from "../utils/awsRum";
import { ApiError } from "../utils/apiError";
import { useAbandonmentDetection } from "../hooks/useAbandonmentDetection";
import { usePageLoadMetric } from "../hooks/usePageLoadMetric";

/**
 * シフトパターンフォームのデータ型（基本情報）
 */
interface ShiftPatternFormData {
    pattern_id: string;
    pattern_name: string;
    display_name: string;
    work_start_time: string;
    work_end_time: string;
    break_distribution: string;
    memo: string;
}

/**
 * シフトパターン登録・編集ページ
 */
const ShiftPatternFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const patternId = id ? parseInt(id, 10) : null;

    const [initialData, setInitialData] = useState<Partial<ShiftPatternFormData> | undefined>(undefined);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentFormData, setCurrentFormData] = useState<ShiftPatternApiData | null>(null);
    const [activeStep, setActiveStep] = useState<ShiftPatternFormStep>(1);
    const [invalidFields, setInvalidFields] = useState<string[]>([]);

    // タグ関連
    const [tags, setTags] = useState<TagMaster[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [tagError, setTagError] = useState<string | null>(null);

    useAbandonmentDetection("shift-pattern-form", currentFormData !== null);
    usePageLoadMetric("shift-pattern-form", !loading);

    const loadTags = useCallback(async () => {
        const list = await fetchShiftPatternTags();
        setTags(
            list.items.map((t) => ({
                id: t.id,
                name: t.name ?? "",
                color: t.color ?? "",
            })),
        );
    }, []);

    // 編集時にシフトパターン本体とタグ情報を取得
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                await loadTags();
                if (isEdit && patternId) {
                    const [data, assignments] = await Promise.all([
                        fetchShiftPattern(patternId),
                        fetchShiftPatternTagAssignments(patternId),
                    ]);
                    if (cancelled) return;
                    setInitialData({
                        pattern_id: data.code,
                        pattern_name: data.name,
                        display_name: data.short_name,
                        work_start_time: data.work_start_time ?? "",
                        work_end_time: data.work_end_time ?? "",
                        break_distribution: data.break_time_minutes ? String(data.break_time_minutes) : "",
                        memo: data.description ?? "",
                    });
                    setSelectedTagIds(assignments.tag_ids);
                }
            } catch (err) {
                if (cancelled) return;
                console.error("Failed to fetch shift pattern data:", err);
                setError(err instanceof Error ? err.message : "データの取得に失敗しました");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [isEdit, patternId, loadTags]);

    const handleFormDataChange = useCallback((data: ShiftPatternApiData) => {
        setCurrentFormData(data);
    }, []);

    const handleCancel = () => {
        setCurrentFormData(null);
        navigate("/store/shift-pattern");
    };

    const handleDelete = async () => {
        if (!patternId) return;
        setDeleting(true);
        setError(null);
        try {
            await deleteShiftPattern(patternId);
            setCurrentFormData(null);
            navigate("/store/shift-pattern");
        } catch (err) {
            console.error("Failed to delete shift pattern:", err);
            setError(err instanceof Error ? err.message : "削除に失敗しました");
            setDeleting(false);
        }
    };

    // 新規モード: 基本情報を作成して一覧へ戻る
    const handleCreateBasicInfo = async () => {
        if (!currentFormData) {
            setError("フォームデータが取得できません");
            recordUserAction("validation_error", { form: "shift-pattern-form", reason: "missing_form_data" });
            return;
        }

        // 必須項目チェック
        const missing: { key: string; label: string }[] = [];
        if (!currentFormData.code?.trim()) missing.push({ key: "pattern_id", label: "ID" });
        if (!currentFormData.name?.trim()) missing.push({ key: "pattern_name", label: "名称" });
        if (!currentFormData.display_name?.trim()) missing.push({ key: "display_name", label: "略称" });
        if (!currentFormData.start_time?.trim()) missing.push({ key: "work_start_time", label: "勤務開始時刻" });
        if (!currentFormData.end_time?.trim()) missing.push({ key: "work_end_time", label: "勤務終了時刻" });
        if (missing.length > 0) {
            setInvalidFields(missing.map((f) => f.key));
            setError(`必須項目が入力されていません: ${missing.map((f) => f.label).join(", ")}`);
            recordUserAction("validation_error", { form: "shift-pattern-form", reason: "required_empty" });
            return;
        }

        setInvalidFields([]);
        setSaving(true);
        setError(null);
        try {
            await createShiftPattern({
                code: currentFormData.code,
                name: currentFormData.name,
                short_name: currentFormData.display_name,
                work_start_time: currentFormData.start_time ?? "",
                work_end_time: currentFormData.end_time ?? "",
                break_time_minutes: currentFormData.break_time_minutes,
                description: currentFormData.memo || null,
            });
            setCurrentFormData(null);
            navigate("/store/shift-pattern");
        } catch (err) {
            console.error("Failed to save shift pattern:", err);
            const message = err instanceof Error ? err.message : "保存に失敗しました";
            setError(message);
            if (err instanceof ApiError) {
                recordUserAction("validation_error", {
                    form: "shift-pattern-form",
                    reason: "save_failed",
                    ...err.toLogDetail(),
                });
            } else {
                recordUserAction("validation_error", { form: "shift-pattern-form", reason: "save_failed", message });
            }
        } finally {
            setSaving(false);
        }
    };

    // 編集モード: 「設定を完了する」で基本情報とタグ紐付けをまとめて保存する
    const handleCompleteEdit = async () => {
        if (!patternId) return;
        if (!currentFormData) {
            setError("フォームデータが取得できません");
            recordUserAction("validation_error", { form: "shift-pattern-form", reason: "missing_form_data" });
            return;
        }

        // 必須項目チェック
        const missing: { key: string; label: string }[] = [];
        if (!currentFormData.code?.trim()) missing.push({ key: "pattern_id", label: "ID" });
        if (!currentFormData.name?.trim()) missing.push({ key: "pattern_name", label: "名称" });
        if (!currentFormData.display_name?.trim()) missing.push({ key: "display_name", label: "略称" });
        if (!currentFormData.start_time?.trim()) missing.push({ key: "work_start_time", label: "勤務開始時刻" });
        if (!currentFormData.end_time?.trim()) missing.push({ key: "work_end_time", label: "勤務終了時刻" });
        if (missing.length > 0) {
            setInvalidFields(missing.map((f) => f.key));
            setError(`必須項目が入力されていません: ${missing.map((f) => f.label).join(", ")}`);
            recordUserAction("validation_error", { form: "shift-pattern-form", reason: "required_empty" });
            setActiveStep(1);
            return;
        }

        setInvalidFields([]);
        setSaving(true);
        setError(null);
        setTagError(null);
        try {
            await updateShiftPattern(patternId, {
                code: currentFormData.code,
                name: currentFormData.name,
                short_name: currentFormData.display_name,
                work_start_time: currentFormData.start_time ?? "",
                work_end_time: currentFormData.end_time ?? "",
                break_time_minutes: currentFormData.break_time_minutes,
                description: currentFormData.memo || null,
            });
            await updateShiftPatternTagAssignments(patternId, selectedTagIds);
            setCurrentFormData(null);
            navigate("/store/shift-pattern");
        } catch (err) {
            console.error("Failed to save shift pattern:", err);
            const message = err instanceof Error ? err.message : "保存に失敗しました";
            setError(message);
            if (err instanceof ApiError) {
                recordUserAction("validation_error", {
                    form: "shift-pattern-form",
                    reason: "save_failed",
                    ...err.toLogDetail(),
                });
            } else {
                recordUserAction("validation_error", { form: "shift-pattern-form", reason: "save_failed", message });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleComplete = (_step: ShiftPatternFormStep) => {
        if (isEdit) {
            handleCompleteEdit();
        } else {
            handleCreateBasicInfo();
        }
    };

    const handleCreateTag = async (data: TagFormValues) => {
        await createShiftPatternTag(data);
        await loadTags();
    };
    const handleUpdateTag = async (tagId: number, data: TagFormValues) => {
        await updateShiftPatternTag(tagId, data);
        await loadTags();
    };
    const handleDeleteTag = async (tagId: number) => {
        await deleteShiftPatternTag(tagId);
        setSelectedTagIds((prev) => prev.filter((id) => id !== tagId));
        await loadTags();
    };

    if (loading) {
        return (
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="px-6 py-4 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-6 bg-brand-500 rounded-full" />
                        <h1 className="text-xl font-bold text-gray-800">
                            シフトパターンの{isEdit ? "編集" : "登録"}
                        </h1>
                    </div>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <span className="text-gray-500">読み込み中...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* ヘッダー */}
            <div className="px-6 py-4 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-6 bg-brand-500 rounded-full" />
                    <h1 className="text-xl font-bold text-gray-800">
                        シフトパターンの{isEdit ? "編集" : "登録"}
                    </h1>
                </div>
                {error && (
                    <div className="mt-2 text-sm text-red-600">
                        {error}
                    </div>
                )}
                {saving && (
                    <div className="mt-2 text-sm text-gray-500">
                        保存中...
                    </div>
                )}
            </div>

            {/* フォームコンテンツ */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
                <ShiftPatternForm
                    onCancel={handleCancel}
                    onComplete={handleComplete}
                    onFormDataChange={handleFormDataChange}
                    initialData={initialData}
                    isEdit={isEdit}
                    onDelete={handleDelete}
                    isDeleting={deleting}
                    isSaving={saving}
                    activeStep={activeStep}
                    onActiveStepChange={setActiveStep}
                    invalidFields={invalidFields}
                    tagSection={{
                        tags,
                        selectedIds: selectedTagIds,
                        onSelectedChange: setSelectedTagIds,
                        onCreate: handleCreateTag,
                        onUpdate: handleUpdateTag,
                        onDelete: handleDeleteTag,
                        errorMessage: tagError,
                        showNewModeNotice: !isEdit,
                    }}
                />
            </div>
        </div>
    );
};

export default ShiftPatternFormPage;
